import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import env from '../config/env';
import permissionService from '../services/permissionService';
import LoadingSpinner from '../components/LoadingSpinner';
import { PermissionContext, useOptionalPermissions } from './permissionContextCore';

/**
 * I permessi dell'utente corrente, caricati una volta al bootstrap.
 *
 * RBAC Fase 3, blocco 4: chiude la divergenza di §2.2. Prima di questo modulo il gating
 * si deduceva da `keycloak.hasRealmRole()` e da liste di path scritte a mano, e nessun
 * test lo collegava alla matrice del backend.
 *
 * A chiamata fallita si nega tutto (nessun permesso) e si alza `status: 'error'`, che
 * `PermissionsUnavailableBanner` mostra all'utente. Non si ricade sui ruoli del token:
 * sarebbe la stessa divergenza, riprodotta proprio durante un guasto.
 */

/**
 * Stato di modulo accanto al contesto: `RoleService` e `routeGuards` sono funzioni pure,
 * chiamate anche fuori dall'albero React, e non possono leggere un hook. Il contesto
 * scrive, questo si legge. `resetPermissions()` esiste perché i test non se lo portino
 * fra un caso e l'altro.
 */
let loadedPermissions = new Set();

export const getLoadedPermissions = () => loadedPermissions;

export const resetPermissions = () => {
  loadedPermissions = new Set();
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() => new Set());
  const [roles, setRoles] = useState([]);
  // Se si puo' agire sulle attivita' prese da un altro: lo decide il backend
  // (`actsOnOthersTasks` di /v1/me/permissions, queues.read a Organizzazione), non un
  // permesso letto qui. Fino al 2026-10-22 la pagina della coda lo ricavava da tasks.update
  // e mostrava pulsanti che rispondevano 403.
  const [actsOnOthersTasks, setActsOnOthersTasks] = useState(false);
  const [status, setStatus] = useState('loading');

  const load = useCallback(async () => {
    // Con l'autenticazione disabilitata (locale/dev) non c'è nessun token da risolvere:
    // l'accesso è libero e RoleService.can() torna true senza consultare questa Set.
    if (!env.useKeycloakAuth) {
      loadedPermissions = new Set();
      setPermissions(new Set());
      setRoles([]);
      setActsOnOthersTasks(true);
      setStatus('ready');
      return;
    }

    setStatus('loading');
    try {
      const data = await permissionService.getMyPermissions();
      const next = new Set(data.permissions || []);
      loadedPermissions = next;
      setPermissions(next);
      setRoles(data.roles || []);
      setActsOnOthersTasks(data.actsOnOthersTasks === true);
      setStatus('ready');
    } catch (error) {
      console.error('Caricamento dei permessi fallito:', error);
      loadedPermissions = new Set();
      setPermissions(new Set());
      setRoles([]);
      setActsOnOthersTasks(false);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Rilettura silenziosa, dopo una modifica che puo' aver cambiato i permessi di chi la fa
  // (una concessione, i permessi di un ruolo). A differenza di `load` non passa da
  // 'loading': lo spinner smonterebbe l'albero, e con esso il pannello su cui l'utente sta
  // lavorando. Chi legge i permessi con `useCan` si ri-renderizza da solo. Se la GET fallisce
  // si tengono i permessi di prima: il backend decide comunque, e negare tutto per un guasto
  // di una rilettura di contorno punirebbe l'utente per una modifica andata a buon fine.
  const refresh = useCallback(async () => {
    if (!env.useKeycloakAuth) {
      return;
    }
    try {
      const data = await permissionService.getMyPermissions();
      const next = new Set(data.permissions || []);
      loadedPermissions = next;
      setPermissions(next);
      setRoles(data.roles || []);
      setActsOnOthersTasks(data.actsOnOthersTasks === true);
      setStatus('ready');
    } catch (error) {
      console.error('Rilettura dei permessi fallita:', error);
    }
  }, []);

  const value = useMemo(
    () => ({ permissions, roles, actsOnOthersTasks, status, reload: load, refresh }),
    [permissions, roles, actsOnOthersTasks, status, load, refresh],
  );

  // Non renderizzare `children` finche' `status === 'loading'`. Non e' cosmesi di
  // caricamento: `children` arriva da App.js con riferimento identico a ogni render, quindi
  // se lo montassimo subito React farebbe bail-out sul sottoalbero quando la GET ritorna, e
  // l'unico a ri-renderizzarsi sarebbe il consumatore del contesto (il banner di errore). Il
  // gating delle rotte pero' si valuta al primo render di ProtectedRoute, con la Set vuota,
  // e una `<Navigate replace />` verso /dashboard non si puo' disfare: senza questo blocco
  // chiunque apra o ricarichi /queues, /prompts, /system-settings o /queues/{id} finirebbe
  // rimbalzato in dashboard mentre i permessi sono ancora in volo, admin compreso.
  // Con `status === 'error'` i figli si renderizzano comunque, cosi' l'utente vede il
  // banner invece di uno spinner infinito. Con `!env.useKeycloakAuth` lo stato passa a
  // 'ready' senza chiamate di rete, quindi l'accesso libero in locale non vede spinner.
  if (status === 'loading') {
    return <LoadingSpinner />;
  }

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions richiede PermissionProvider');
  }
  return context;
};

export { useOptionalPermissions };
